import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useFacilities } from "@/hooks/useFacilities";

// ── Types ───────────────────────────────────────────────────────────────

export interface FacilityZone {
  id: string;
  org_id: string;
  facility_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateFacilityZoneInput {
  facility_id: string;
  parent_id?: string | null;
  name: string;
  description?: string;
  sort_order?: number;
}

export interface UpdateFacilityZoneInput {
  facility_id?: string;
  parent_id?: string | null;
  name?: string;
  description?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

// ── Queries ─────────────────────────────────────────────────────────────

export function useFacilityZones(facilityId?: string) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  return useQuery({
    queryKey: ["facility-zones", facilityId],
    queryFn: async () => {
      let query = supabase
        .from("facility_zone" as any)
        .select("*")
        .eq("is_active", true)
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (facilityId) {
        query = query.eq("facility_id", facilityId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as FacilityZone[];
    },
    enabled: isAuthenticated && !authLoading,
  });
}

// ── All zones with facility label ────────────────────────────────────────

export interface ZoneWithFacility extends FacilityZone {
  facility_code: string;
  facility_name: string;
  /** Display label: "GWH › Annex" */
  label: string;
}

/** Returns all active zones across all facilities, each enriched with
 *  facility code/name and a combined display label ("GWH › Annex"). */
export function useAllZonesFlat(): ZoneWithFacility[] {
  const { data: zones = [] } = useFacilityZones();
  const { data: facilities = [] } = useFacilities();

  return zones.map((z) => {
    const fac = facilities.find((f) => f.id === z.facility_id);
    return {
      ...z,
      facility_code: fac?.code ?? "",
      facility_name: fac?.name ?? "",
      label: fac ? `${fac.code} › ${z.name}` : z.name,
    };
  });
}

// ── Part zone stock ───────────────────────────────────────────────────────

export type PartZoneStock = {
  zone_id: string;
  zone_name: string;
  facility_code: string;
  facility_name: string;
  quantity: number;
};

const ZONE_STOCK_KEY = (partId: string) => ["part-zone-stock", partId] as const;

export function usePartZoneStock(partId: string): PartZoneStock[] {
  const { data } = useQuery({
    queryKey: ZONE_STOCK_KEY(partId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("part_zone_stock" as any)
        .select(`
          zone_id,
          quantity,
          facility_zone!inner (
            name,
            facility:facility_id ( code, name )
          )
        `)
        .eq("part_id", partId);
      if (error) throw error;
      return (data ?? []).map((row: any) => ({
        zone_id: row.zone_id,
        zone_name: row.facility_zone.name,
        facility_code: row.facility_zone.facility.code,
        facility_name: row.facility_zone.facility.name,
        quantity: row.quantity,
      })) as PartZoneStock[];
    },
    enabled: !!partId,
  });
  return data ?? [];
}

export type UpsertZoneStockEntry = {
  part_id: string;
  zone_id: string;
  quantity: number;
  org_id: string;
};

export function useUpsertPartZoneStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entry: UpsertZoneStockEntry) => {
      const { error } = await supabase
        .from("part_zone_stock" as any)
        .upsert({ ...entry }, { onConflict: "part_id,zone_id" });
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ZONE_STOCK_KEY(vars.part_id) });
    },
  });
}

export function useDeletePartZoneStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ partId, zoneId }: { partId: string; zoneId: string }) => {
      const { error } = await supabase
        .from("part_zone_stock" as any)
        .delete()
        .eq("part_id", partId)
        .eq("zone_id", zoneId);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ZONE_STOCK_KEY(vars.partId) });
    },
  });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreateFacilityZone() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateFacilityZoneInput) => {
      const { data: userData, error: userError } = await supabase
        .from("user_account")
        .select("org_id")
        .eq("external_auth_user", (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (userError || !userData) throw new Error("Failed to get user organization");

      const { data, error } = await supabase
        .from("facility_zone" as any)
        .insert([{
          org_id: userData.org_id,
          facility_id: input.facility_id,
          parent_id: input.parent_id || null,
          name: input.name,
          description: input.description || null,
          sort_order: input.sort_order || 0,
        }])
        .select()
        .single();

      if (error) throw error;
      return data as FacilityZone;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility-zones"] });
      toast({ title: "Zone created", description: "The facility zone has been created." });
    },
    onError: (error: Error) => {
      toast({ title: "Error creating zone", description: error.message, variant: "destructive" });
    },
  });
}

export function useUpdateFacilityZone() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateFacilityZoneInput }) => {
      const { data, error } = await supabase
        .from("facility_zone" as any)
        .update(input)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data as FacilityZone;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility-zones"] });
      toast({ title: "Zone updated", description: "The facility zone has been updated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error updating zone", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteFacilityZone() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("facility_zone" as any)
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["facility-zones"] });
      toast({ title: "Zone deactivated", description: "The facility zone has been deactivated." });
    },
    onError: (error: Error) => {
      toast({ title: "Error deactivating zone", description: error.message, variant: "destructive" });
    },
  });
}
